import { useState } from "react";
import { ChevronDown, ChevronRight, PanelTop } from "lucide-react";

import MergeFieldEditor from "./MergeFieldEditor";

/**
 * The page header and footer printed on every page of the PDF — a letterhead
 * logo, the company address, "Page {{page_number}} of {{page_count}}".
 * Blank keeps the built-in band (company and MOU number above, page X of Y
 * below). Collapsed until opened unless one is already set.
 */
export default function HeaderFooterEditor({ header, footer, onChange, fields = [], bandFields = [] }) {
  const [open, setOpen] = useState(Boolean(header || footer));
  const all = [...bandFields, ...fields];
  const isSet = Boolean(header || footer);

  return (
    <div className="rounded-md border border-gray-200 dark:border-gray-700">
      <button type="button" onClick={() => setOpen((o) => !o)}
              className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-200">
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <PanelTop className="h-3.5 w-3.5 text-gray-400" /> Page header &amp; footer
        <span className="font-normal text-gray-500 dark:text-gray-400">
          — {isSet ? "custom" : "built-in (company name, MOU number, page X of Y)"}
        </span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-gray-200 p-3 dark:border-gray-700">
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            Printed on every page. Add a logo with the image button; keep each to two or three
            short lines so it fits the page band. Use{" "}
            <code className="rounded bg-gray-100 px-1 dark:bg-gray-700">{"{{page_number}}"}</code> and{" "}
            <code className="rounded bg-gray-100 px-1 dark:bg-gray-700">{"{{page_count}}"}</code> for
            "Page 1 of 5". Leave both empty for the built-in header and footer.
          </p>
          <div className="space-y-1">
            <label className="text-xs font-medium">Header</label>
            <MergeFieldEditor compact images tables value={header} fields={all} height={130}
                              placeholder="e.g. your logo, company name and address"
                              onChange={(v) => onChange({ header: v })} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Footer</label>
            <MergeFieldEditor compact images tables value={footer} fields={all} height={110}
                              placeholder="e.g. Confidential · Page {{page_number}} of {{page_count}}"
                              onChange={(v) => onChange({ footer: v })} />
          </div>
        </div>
      )}
    </div>
  );
}

/** Quill's idea of empty (<p><br></p>) counts as no custom band. */
export const isBlankHtml = (html) =>
  !(html || "").includes("<img") && !(html || "").replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim();
